import { useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { ConfirmAction } from '@eis/patterns';
import { useTerminateGroupPolicy } from '../api/group-policies';
import { ConflictNotice } from '../../policies/ui/ConflictNotice';

/**
 * Beenden — irreversible, dated, and NOT a cascade.
 *
 * Terminating the master leaves its members in force. Each child-policy has its own cancellation
 * right (decision S-11, SSOT I-2), and the surviving members' billing path is rescued in the same
 * transaction as the master's termination (issue I-9) — by the SERVICE, not by the console. The
 * copy states that outcome rather than leaving the operator to infer it from a checkbox that is
 * not there: the opt-in cascade needs its own dry run, and this dialog does not offer it.
 *
 * The typed guard stays: the agreement's code must be entered before the action unlocks. Live
 * cover is being ended, and a confirmation dialog that only needs a click is a dialog people
 * click through.
 */
export function TerminationDialog({
  code,
  open,
  onClose,
}: {
  readonly code: string;
  readonly open: boolean;
  readonly onClose: () => void;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const terminate = useTerminateGroupPolicy();
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [reason, setReason] = useState('');

  const close = (): void => {
    setEffectiveFrom('');
    setReason('');
    terminate.reset();
    onClose();
  };

  return (
    <ConfirmAction
      open={open}
      severity="destructive"
      title={t('groupPolicies.termination.title')}
      description={t('groupPolicies.termination.confirmDescription')}
      confirmLabel={t('groupPolicies.termination.confirm')}
      // The typed guard: terminating live cover is irreversible, so the code is typed out.
      typedConfirmationPhrase={code}
      busy={terminate.isPending}
      onCancel={close}
      onConfirm={() => {
        if (effectiveFrom === '') return;
        terminate.mutate({ code, effectiveFrom, reason }, { onSuccess: close });
      }}
    >
      <Stack spacing={1.5}>
        <ConflictNotice conflict={terminate.conflict} />

        <TextField
          type="date"
          label={t('groupPolicies.termination.effectiveDate')}
          value={effectiveFrom}
          required
          fullWidth
          slotProps={{ inputLabel: { shrink: true } }}
          onChange={(event) => {
            setEffectiveFrom(event.target.value);
          }}
          helperText={t('groupPolicies.termination.effectiveDateHint')}
        />

        <TextField
          label={t('groupPolicies.termination.reason')}
          value={reason}
          fullWidth
          onChange={(event) => {
            setReason(event.target.value);
          }}
        />

        <Alert severity="info">
          <Typography variant="body2">{t('groupPolicies.termination.noCascade')}</Typography>
        </Alert>

        {terminate.isError && terminate.conflict === null && (
          <Alert severity="error">{terminate.error.message}</Alert>
        )}
      </Stack>
    </ConfirmAction>
  );
}
